import { SiInstagram, SiTiktok } from '@icons-pack/react-simple-icons';
import { createElement } from 'react';

/** Placeholder for unavailable data */
export const NO_DATA = '\u2014';

export const isFiniteNumber = (num: number | null | undefined): num is number =>
  typeof num === 'number' && Number.isFinite(num);

/**
 * Format large numbers with K/M suffix
 */
export const formatNumber = (num: number | null | undefined): string => {
  if (!isFiniteNumber(num)) return '—';
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

/**
 * Format percentage with 1 decimal
 */
export const formatPercentage = (num: number | null | undefined): string => {
  if (!isFiniteNumber(num)) return '—';
  return `${num.toFixed(1)}%`;
};

/**
 * Formats the canonical discovery engagement ratio for presentation.
 *
 * The public discovery DTO always carries a ratio: `0.0028` means `0.28%`.
 * Keep the conversion here, at the UI boundary, so cards and the profile drawer
 * cannot each apply a different convention or infer an old unit from its value.
 */
export const formatEngagementRate = (ratio: number | null | undefined): string | null => {
  if (!isFiniteNumber(ratio)) return null;
  return formatPercentage(ratio * 100);
};

/** A calculated brand affinity is a score on the inclusive 0–100 scale. */
export const formatBrandAffinityScore = (score: number | null | undefined): string | null => {
  if (!isFiniteNumber(score)) return null;
  return `${Math.round(score)}/100`;
};

/**
 * Format age bucket to human-readable format
 * Converts "18_to_24" -> "18-24", "65_and_above" -> "65+"
 */
export const formatAgeBucket = (ageBucket: string | undefined): string | null => {
  if (!ageBucket) return null;
  return ageBucket.replace(/_to_/g, '-').replace(/_and_above/g, '+');
};

/**
 * Get platform icon component
 */
export const getPlatformIcon = (platform: string | undefined, size: number = 14) => {
  switch (platform?.toLowerCase()) {
    case 'instagram':
      return createElement(SiInstagram, { size, title: 'Instagram' });
    case 'tiktok':
      return createElement(SiTiktok, { size, title: 'TikTok' });
    default:
      return null;
  }
};

/**
 * Get the platform's display name (a proper noun, identical across locales).
 */
export const getPlatformName = (platform: string | undefined): string => {
  switch (platform?.toLowerCase()) {
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    default:
      return '';
  }
};

const sanitizeSocialHandle = (username: string): string => username.trim().replace(/^@+/, '');

/**
 * Get platform profile URL
 */
export const getPlatformProfileUrl = (
  platform: string | undefined,
  username: string | undefined
): string | undefined => {
  if (!platform || !username) return undefined;

  const cleanUsername = sanitizeSocialHandle(username);
  if (!cleanUsername) return undefined;

  const encodedUsername = encodeURIComponent(cleanUsername);

  switch (platform.toLowerCase()) {
    case 'instagram':
      return `https://instagram.com/${encodedUsername}`;
    case 'tiktok':
      return `https://tiktok.com/@${encodedUsername}`;
    default:
      return undefined;
  }
};
